<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// Fix for passive event listener warning
// Makes wheel/touch events passive by default for better scroll performance
;(function() {
  if (typeof EventTarget !== 'undefined') {
    const originalAddEventListener = EventTarget.prototype.addEventListener
    EventTarget.prototype.addEventListener = function(type, listener, options) {
      const passiveEvents = ['wheel', 'mousewheel', 'touchstart', 'touchmove']
      if (passiveEvents.includes(type)) {
        let newOptions = options
        if (typeof options === 'boolean') {
          newOptions = { capture: options, passive: true }
        } else if (typeof options === 'object' || options === undefined) {
          newOptions = { ...options, passive: options?.passive !== false }
        }
        return originalAddEventListener.call(this, type, listener, newOptions)
      }
      return originalAddEventListener.call(this, type, listener, options)
    }
  }
})()

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, onMounted } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { updateAbilities } from '@/libs/acl'
import {
  STORAGE_KEY as TASK_PING_STORAGE_KEY,
  checkAndToastUnreadTaskPings,
  startTaskPingPolling,
  stopTaskPingPolling,
  resumeTaskPingPollingAfterAuth,
} from '@/utils/task-ping-notifier'
import {
  ensureFcmReady as initGlobalFcm,
} from '@/utils/fcm-push'

// console.log('App.vue - updateAbilities imported:', updateAbilities)

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  watch: {
    // Surface unread manager pings on any in-app navigation (not only Task Management).
    // Also resumes task/notification polling if a prior 401/403 paused it.
    $route(to, from) {
      this.checkTaskPingToasts()
      // After login, App is already mounted — re-attempt token registration +
      // foreground FCM handler once userData exists.
      this.ensureFcmReady()
      // Mount skips notifications/refresh on full-layout (login); load inbox once
      // when entering the authenticated shell.
      const fromFull = !!(from && from.meta && from.meta.layout === 'full')
      const toFull = !!(to && to.meta && to.meta.layout === 'full')
      if (fromFull && !toFull && localStorage.getItem('userData')) {
        this.$store.dispatch('notifications/refresh').catch(() => {})
      }
    },
  },
  mounted() {
    // Sync session in-memory inbox into Vuex, then GET /api/notifications
    // (server truth when available). Poller keeps task pings/escalations in sync.
    // Skip API refresh on full-layout (login) — no session / would 401-spam.
    this.$store.dispatch('notifications/hydrate')
    if (!(this.$route && this.$route.meta && this.$route.meta.layout === 'full')) {
      this.$store.dispatch('notifications/refresh').catch(() => {})
    }
    // Poll GET /pm/tasks so a manager ping on another browser reaches this
    // employee without opening Task Management (localStorage cannot cross users).
    // Same poll also syncs the notification inbox (API POST bridge + GET).
    startTaskPingPolling(this)
    window.addEventListener('storage', this.onTaskPingStorage)
    // Global FCM: permission + token + foreground toast/inbox + SW click routing.
    // Not tied to Calls / Tasks / Attendance pages.
    initGlobalFcm(this)
  },
  beforeDestroy() {
    stopTaskPingPolling()
    window.removeEventListener('storage', this.onTaskPingStorage)
  },
  methods: {
    /**
     * Sticky assignee toast for unread Task pings — global, not Task-page-only.
     * Also refreshes the navbar notification inbox. See `task-ping-notifier.js`.
     * Resumes polling after an auth soft-fail pause (login / navigation).
     */
    checkTaskPingToasts() {
      resumeTaskPingPollingAfterAuth(this)
    },
    /** Same-browser cross-tab only — not the cross-user path. */
    onTaskPingStorage(event) {
      if (event && event.key === TASK_PING_STORAGE_KEY) {
        checkAndToastUnreadTaskPings(this, { forceFetch: true })
      }
    },
    /**
     * Register FCM device token + bind foreground handler when a user session
     * exists. Runs from App root (mount + every route) so push works on any
     * page after login. Graceful no-op if permission denied / unsupported.
     */
    ensureFcmReady() {
      initGlobalFcm(this)
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    // Hydrate CASL from localStorage on refresh / already-logged-in boot.
    // ability.js also seeds from localStorage at import; this keeps the
    // singleton in sync if userData was written after the module loaded
    // (e.g. another tab) and does not delay login (Login.vue updates abilities
    // before navigating).
    onMounted(() => {
      try {
        const userData = JSON.parse(localStorage.getItem('userData'))
        if (userData && userData.abilities) {
          updateAbilities(userData.abilities)
        }
      } catch (error) {
        // ignore corrupt userData
      }
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },

}
</script>
